// Copyright 2025-2026 Florian Gallon. All Rights reserved.
import React, { useState, useEffect, useRef, Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation, useParams, Navigate } from 'react-router-dom';
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { LogOut, Home, ShoppingCart, User, Users, Settings, ChevronDown, FileText, HelpCircle, Shield, AlertCircle, CreditCard, Menu, X, Sun, Moon, Plus, Calendar, BarChart2, MoreHorizontal, MapPin } from 'lucide-react';
import { useLanguage } from './contexts/LanguageContext';
import { useTheme } from './contexts/ThemeContext';
import { t } from './translations';
import { hasActiveSubscription, getGracePeriodInfo } from './utils/stripeUtils';
import { migrateIngredients } from './utils/ingredientMigration';
import {
  getCurrentBakeryContext,
  shouldRedirectToFriendlyURL,
  redirectToFriendlyURL,
  shouldRedirectToCustomDomain,
  redirectToCustomDomain,
  getBakery
} from './utils/routingUtils';
import { logAdminAction } from './utils/adminUtils';
import { touchLastActive } from './utils/userActivity';

// Import components
import Button from './components/Button';
import ErrorBoundary from './components/ErrorBoundary';
import AuthScreen from './components/AuthScreen';             // eager: critical login path
import PasswordResetPage from './components/PasswordResetPage'; // eager: per requirements
import CommercialLandingPage from './components/CommercialLandingPage'; // eager: rendered outside <Routes>
import ProtectedBakerRoute from './components/ProtectedBakerRoute'; // eager: synchronous route wrapper
import NotificationsBell from './components/NotificationsBell'; // eager: always-visible nav element
import AppShell from './components/AppShell';
import Alert from './components/ui/Alert';
import { NotificationsProvider } from './contexts/NotificationsContext';

// Lazy-loaded route components (split into separate JS chunks)
// Customer / public routes
const HomeView               = React.lazy(() => import('./components/HomeView'));
const ProfileView            = React.lazy(() => import('./components/ProfileView'));
const ShopView               = React.lazy(() => import('./components/ShopView2')); // Editorial design (permanent)
const OrdersView             = React.lazy(() => import('./components/OrdersView'));
const OrderConfirmationView  = React.lazy(() => import('./components/OrderConfirmationView'));
const PrivacyPolicy          = React.lazy(() => import('./components/PrivacyPolicy'));

// Baker routes
const ProductManagement      = React.lazy(() => import('./components/ProductManagement'));
const RecipeManager          = React.lazy(() => import('./components/RecipeManager'));
const EquipmentManager       = React.lazy(() => import('./components/EquipmentManager'));
const FixedCostsManagement   = React.lazy(() => import('./components/FixedCostsManagement'));
const ProductionDaysManagement = React.lazy(() => import('./components/ProductionDaysManagement'));
const ProductionPlanning     = React.lazy(() => import('./components/ProductionPlanning'));
const BakerOrdersView        = React.lazy(() => import('./components/BakerOrdersView'));
const PickupMode             = React.lazy(() => import('./components/PickupMode'));
const BakerOrderRegistration = React.lazy(() => import('./components/BakerOrderRegistration'));
const ReportsView            = React.lazy(() => import('./components/ReportsView'));
const WasteReportsView       = React.lazy(() => import('./components/WasteReportsView'));
const ShopAnalyticsView      = React.lazy(() => import('./components/ShopAnalyticsView'));
const BakerSettings          = React.lazy(() => import('./components/BakerSettings'));
const HomePageEditor         = React.lazy(() => import('./components/HomePageEditor'));
const ProductionTemplates    = React.lazy(() => import('./components/ProductionTemplates'));
const ShoppingList           = React.lazy(() => import('./components/ShoppingList'));
const StockManagement        = React.lazy(() => import('./components/StockManagement'));
const PhysicalInventory       = React.lazy(() => import('./components/PhysicalInventory'));
const InventoryAdjustmentsView = React.lazy(() => import('./components/InventoryAdjustmentsView'));
const HelpView               = React.lazy(() => import('./components/HelpView'));
const AnnouncementManager    = React.lazy(() => import('./components/AnnouncementManager'));
const SalesReconciliation    = React.lazy(() => import('./components/SalesReconciliation'));
const WeatherSalesAnalysis   = React.lazy(() => import('./components/WeatherSalesAnalysis'));
const BakerNotificationsView = React.lazy(() => import('./components/BakerNotificationsView'));
const CustomerManagement     = React.lazy(() => import('./components/CustomerManagement'));
const AcceptCustomerInvite   = React.lazy(() => import('./components/AcceptCustomerInvite'));

// Admin routes
const AdminDashboard         = React.lazy(() => import('./components/AdminDashboard'));
const AdminBakeriesView      = React.lazy(() => import('./components/AdminBakeriesView'));
const AdminBakeryDetail      = React.lazy(() => import('./components/AdminBakeryDetail'));
const AdminUsersView         = React.lazy(() => import('./components/AdminUsersView'));
const AdminAnalytics         = React.lazy(() => import('./components/AdminAnalytics'));
const AdminOrdersView        = React.lazy(() => import('./components/AdminOrdersView'));
const AdminProfileSettings   = React.lazy(() => import('./components/AdminProfileSettings'));
const DatabaseCleaner        = React.lazy(() => import('./components/DatabaseCleaner'));

// Payment routes (also loads @stripe/stripe-js only when needed)
const PaymentForm            = React.lazy(() => import('./components/PaymentForm'));
const PaymentSuccess         = React.lazy(() => import('./components/PaymentSuccess'));
const PaymentCanceled        = React.lazy(() => import('./components/PaymentCanceled'));

// Demo imports removed

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showShopMenu, setShowShopMenu] = useState(false);
  const [showBakeryMenu, setShowBakeryMenu] = useState(false);
  const [showProductionMenu, setShowProductionMenu] = useState(false);
  const [showReportsMenu, setShowReportsMenu] = useState(false);
  const [isMobileNavCollapsed, setIsMobileNavCollapsed] = useState(true);
  const [showMobileMore, setShowMobileMore] = useState(false);
  const [currentBakeryId, setCurrentBakeryId] = useState(null);
  const [currentBakery, setCurrentBakery] = useState(null);
  const [impersonationContext, setImpersonationContext] = useState(null);
  const [bakerSettings, setBakerSettings] = useState(null);
  const [bakeryContext, setBakeryContext] = useState({});
  const [gracePeriodInfo, setGracePeriodInfo] = useState(null);
  const [showGracePeriod, setShowGracePeriod] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const { theme, setTheme } = useTheme();
  const orderConfirmationDataRef = useRef(null);
  // Profile already read for the signed-in user ({ uid, profile }) and the bakery currently in state;
  // the auth effect re-subscribes on every route change, so these spare a Firestore round-trip per navigation
  const loadedProfileRef = useRef(null);
  const currentBakeryRef = useRef(null);
  currentBakeryRef.current = currentBakery;

  // Function to refresh bakery data
  const refreshBakeryData = async (bakeryId) => {
    if (bakeryId) {
      const bakeryData = await getBakery(bakeryId);
      setCurrentBakery(bakeryData);

      // Load baker settings for SEO metadata
      if (bakeryData?.ownerId) {
        try {
          const settingsDoc = await getDoc(doc(db, 'bakerSettings', bakeryData.ownerId));
          if (settingsDoc.exists()) {
            setBakerSettings(settingsDoc.data());
          }
        } catch (error) {
          console.error('Error loading baker settings:', error);
        }
      }
    }
  };

  // Initialize routing and bakery context
  useEffect(() => {
    const initializeRouting = async () => {
      try {
        const context = await getCurrentBakeryContext();
        setBakeryContext(context);


        if (context.bakeryId) {
           setCurrentBakeryId(context.bakeryId);
           setCurrentBakery(context.bakery);

           // Redirect from legacy URL to friendly URL if needed
           if (shouldRedirectToFriendlyURL(context)) {
             redirectToFriendlyURL(context.bakery);
             return; // Stop execution as we're redirecting
           }

           // Redirect to custom domain if bakery has one (SEO consolidation)
           if (shouldRedirectToCustomDomain(context)) {
             redirectToCustomDomain(context.bakery);
             return; // Stop execution as we're redirecting
           }
         }

        // For React Router, we don't set an initial view state
        // The routing will be handled by the Routes component

      } catch (error) {
        console.error('❌ Error initializing routing:', error);
      }
    };

    initializeRouting();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle user authentication and routing
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      try {
        if (user) {
          const cachedProfile = loadedProfileRef.current?.uid === user.uid ? loadedProfileRef.current.profile : null;
          let profile = cachedProfile;
          if (!profile) {
            const profileDoc = await getDoc(doc(db, 'users', user.uid));
            if (profileDoc.exists()) {
              profile = profileDoc.data();
              loadedProfileRef.current = { uid: user.uid, profile };
              setUserProfile(profile);
            }
          }
          if (profile) {

            // Handle admin routing
            if (profile.role === 'admin') {
              // Admin users don't have a bakery context, redirect to admin dashboard
              if (['/', '/login', '/signup-baker', '/commercial-landing'].includes(location.pathname)) {
                navigate('/admin');
              }
            } else if (profile.role === 'baker' && profile.bakeryId) {
              if (!cachedProfile) touchLastActive(user.uid, profile); // not awaited: shown as "last connection" in admin
              // Set baker's bakery as current bakery for context
              setCurrentBakeryId(profile.bakeryId);
              if (!cachedProfile || currentBakeryRef.current?.id !== profile.bakeryId) {
                await refreshBakeryData(profile.bakeryId);
              }

              // Only navigate if we're on auth pages or main page
              if (['/', '/login', '/signup-baker', '/commercial-landing'].includes(location.pathname)) {
                const isSubdomain = bakeryContext.routingMethod === 'subdomain';
                if (bakeryContext.bakeryId === profile.bakeryId) {
                  // Baker is on their own bakery URL, show management dashboard
                  navigate(isSubdomain ? '/baker-orders' : `/${bakeryContext.bakery?.subdomain || profile.bakeryId}/baker-orders`);
                } else if (!bakeryContext.bakeryId) {
                  // Baker on main site, redirect to their bakery management
                  navigate(`/${currentBakery?.subdomain || profile.bakeryId}/baker-orders`);
                } else {
                  // Baker on someone else's bakery, show customer view
                  navigate(isSubdomain ? '/shop' : `/${bakeryContext.bakery?.subdomain || bakeryContext.bakeryId}/shop`);
                }
              }
            } else if (profile.role === 'customer') {
              // Customer routing - only navigate if we're on auth pages
              if (['/', '/login', '/commercial-landing'].includes(location.pathname)) {
                // Honor returnTo parameter (e.g. from accept-customer-invite flow)
                const returnTo = new URLSearchParams(location.search).get('returnTo');
                if (returnTo) {
                  navigate(returnTo);
                } else if (bakeryContext.isCustomerFacing) {
                  // On a bakery-specific URL, show shop
                  const isSubdomain = bakeryContext.routingMethod === 'subdomain';
                  navigate(isSubdomain ? '/shop' : `/${bakeryContext.bakery?.subdomain || bakeryContext.bakeryId}/shop`);
                } else {
                  // Customer on main site
                  navigate('/home');
                }
              }

              // Check if there's pending order confirmation data
              const orderData = localStorage.getItem('orderConfirmationData');
              if (orderData) {
                orderConfirmationDataRef.current = JSON.parse(orderData);
                localStorage.removeItem('orderConfirmationData');
                navigate('/order-confirmation');
              }
            }
          }
        } else {
          loadedProfileRef.current = null;
          setUserProfile(null);
          setImpersonationContext(null);

          // For non-authenticated users, let the routes handle the routing naturally
          // Don't add any redirects here - let the Routes component decide what to show
        }
      } catch (error) {
        // A failed read must not leave the app stuck on the loading screen until a manual refresh
        console.error('Error loading user profile:', error);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [bakeryContext, currentBakeryId, navigate, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-time ingredient migration for bakers
  useEffect(() => {
    if (currentUser && userProfile?.role === 'baker') {
      migrateIngredients(currentUser.uid)
        .then(result => {
          if (result.status === 'done') {
            console.log('[App] Ingredient migration complete:', result);
          }
        })
        .catch(err => console.error('[App] Migration error:', err));
    }
  }, [currentUser, userProfile]);

  // Handle click outside for menus
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAccountMenu && !event.target.closest('.account-menu-container')) {
        setShowAccountMenu(false);
      }
      if (showShopMenu && !event.target.closest('.shop-menu-container')) {
        setShowShopMenu(false);
      }
      if (showBakeryMenu && !event.target.closest('.bakery-menu-container')) {
        setShowBakeryMenu(false);
      }
      if (showProductionMenu && !event.target.closest('.production-menu-container')) {
        setShowProductionMenu(false);
      }
      if (showReportsMenu && !event.target.closest('.reports-menu-container')) {
        setShowReportsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAccountMenu, showShopMenu, showBakeryMenu, showProductionMenu, showReportsMenu]);

  // Keep baker/customer nav collapsed on phones after route changes to maximize workspace.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setIsMobileNavCollapsed(true);
      setShowMobileMore(false);
    }
  }, [location.pathname]);

  // Check grace period for bakers
  useEffect(() => {
    const checkGracePeriod = async () => {
      // Only check for bakers
      if (!currentUser || !userProfile || userProfile.role !== 'baker') {
        setShowGracePeriod(false);
        return;
      }

      try {
        // Get bakery data
        const bakeryDoc = await getDoc(doc(db, 'bakeries', currentUser.uid));
        if (!bakeryDoc.exists()) {
          setShowGracePeriod(false);
          return;
        }

        const bakeryData = bakeryDoc.data();

        // Check if grandfathered (old bakeries don't need subscription)
        if (bakeryData.grandfathered === true) {
          setShowGracePeriod(false);
          return;
        }

        // Check subscription status
        const hasSubscription = hasActiveSubscription(bakeryData);

        // Calculate grace period info (checks both database status AND date)
        const gracePeriod = getGracePeriodInfo(bakeryData);
        setGracePeriodInfo(gracePeriod);

        // Grace period is active ONLY if:
        // 1. Database status is 'grace_period' AND
        // 2. Actual date calculation shows it hasn't expired
        const isInGracePeriod = bakeryData.subscriptionStatus === 'grace_period' && gracePeriod?.isInGracePeriod;


        // Show banner if no subscription and in grace period
        const shouldShowBanner = !hasSubscription && isInGracePeriod;
        setShowGracePeriod(shouldShowBanner);

        // Handle grace period expiration - redirect to subscription page
        const gracePeriodExpired = !hasSubscription && !isInGracePeriod;
        if (gracePeriodExpired && !location.pathname.includes('/settings')) {
          navigate(`/${currentBakery?.subdomain || currentBakeryId}/settings?tab=subscription&expired=true`);
        }
      } catch (error) {
        console.error('Error checking grace period:', error);
        setShowGracePeriod(false);
      }
    };

    checkGracePeriod();
  }, [currentUser, userProfile, currentBakery, currentBakeryId, location.pathname, navigate]);

  const handleLogout = async () => {
    await signOut(auth);

    // Determine where to redirect after logout
    if (bakeryContext.isCustomerFacing) {
      navigate(`/${currentBakery?.subdomain || currentBakeryId}/shop`); // Stay on the bakery shop
    } else {
      navigate('/commercial-landing'); // Go to main commercial page
    }

    setShowAccountMenu(false);
  };

  const handleHeaderThemeToggle = () => {
    const currentAppliedTheme = document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
    setTheme(currentAppliedTheme === 'dark' ? 'light' : 'dark');
  };

  // Customer shop should default to light mode for non-baker users.
  useEffect(() => {
    const isNonBakerUser = !currentUser || userProfile?.role === 'customer';
    const isCustomerShopRoute = location.pathname.includes('/shop');

    if (isNonBakerUser && isCustomerShopRoute && theme !== 'light') {
      setTheme('light');
    }
  }, [currentUser, userProfile, location.pathname, theme, setTheme]);

  // Baker login default: if no persisted baker theme exists, start in light mode.
  useEffect(() => {
    const isBaker = !!currentUser && userProfile?.role === 'baker';
    const hasSavedBakerTheme = !!bakerSettings?.theme;

    if (isBaker && !hasSavedBakerTheme && theme !== 'light') {
      setTheme('light');
    }
  }, [currentUser, userProfile, bakerSettings, theme, setTheme]);

  const goToShop = () => {
    navigate(bakeryContext.bakeryId ? `/${currentBakery?.subdomain || currentBakeryId}/shop` : '/shop');
  };

  // Handle hash-based navigation for login
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#login') {
        navigate('/login');
        window.location.hash = ''; // Clear the hash
      }
    };

    const handleNavigateToLogin = () => {
      navigate('/login');
    };

    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('navigateToLogin', handleNavigateToLogin);
    handleHashChange(); // Check on mount

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('navigateToLogin', handleNavigateToLogin);
    };
  }, [navigate]);

  // Track sticky header height so modals can start below it on iPad
  useEffect(() => {
    const header = document.querySelector('.app-header');
    if (!header) return;
    const observer = new ResizeObserver(() => {
      document.documentElement.style.setProperty(
        '--app-header-height',
        `${header.getBoundingClientRect().height}px`
      );
    });
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  // Function to handle settings saved and refresh bakery
  const handleSettingsSaved = async () => {
    if (currentBakeryId) {
      await refreshBakeryData(currentBakeryId);
    }
  };

  const handleStartImpersonation = async (targetBakery) => {
    const ctx = {
      bakeryId: targetBakery.id,
      bakery: targetBakery,
      bakerEmail: targetBakery.ownerInfo?.email || '',
      bakerName: targetBakery.ownerInfo?.name || targetBakery.name,
      returnPath: `/admin/bakeries/${targetBakery.id}`,
    };
    setImpersonationContext(ctx);
    await logAdminAction(currentUser.uid, 'startImpersonation', targetBakery.id, {
      bakeryName: targetBakery.name,
      bakerEmail: ctx.bakerEmail,
    });
    navigate(`/${targetBakery.subdomain || targetBakery.id}/baker-orders`);
  };

  const handleExitImpersonation = async () => {
    const returnPath = impersonationContext?.returnPath || '/admin/bakeries';
    await logAdminAction(currentUser.uid, 'stopImpersonation', impersonationContext?.bakeryId, {
      bakeryName: impersonationContext?.bakery?.name,
      bakerEmail: impersonationContext?.bakerEmail,
    });
    setImpersonationContext(null);
    navigate(returnPath);
  };

  // Editorial navigation button classes
const getNavButtonClasses = (isActive) => {
  const base =
    "nav-item cursor-pointer px-5 py-2.5 rounded-full whitespace-nowrap font-ui text-sm font-semibold uppercase tracking-[0.03em] flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]";

  // Let CSS own active visuals (bg/border/text)
  const active =
    "is-active border border-[var(--border-subtle)]";

  const inactive =
    "text-[var(--text-secondary)] border border-transparent";

  return `${base} ${isActive ? active : inactive}`;
};

  // 👈 RESTORED: Original getMenuButtonClasses from working version
  const getMenuButtonClasses = (isActive = false) => {
    const base = "nav-dropdown-item menu-item cursor-pointer w-full text-left px-4 py-2.5 rounded-xl border transition-colors duration-200 font-ui text-sm font-semibold uppercase tracking-[0.03em] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]";
    const active = "is-active bg-[var(--action-primary-soft)] text-[var(--text-primary)] border border-[var(--border-subtle)]";
    const inactive = "bg-transparent text-[var(--text-secondary)] border border-transparent hover:bg-[var(--action-ghost-hover)] hover:text-[var(--text-primary)]";
    return `${base} ${isActive ? active : inactive}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]">
        <div className="text-center">
          <img src="/Logo-digi-bakery-512.png" alt="Logo" className="w-24 h-24 mb-4 animate-bounce mx-auto" />
          <p className="text-xl text-[var(--text-secondary)] font-medium">
            {language === 'fr' ? 'Chargement...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }


  const isImpersonating = !!impersonationContext;
  const effectiveBakeryId = isImpersonating ? impersonationContext.bakeryId : currentBakeryId;
  const effectiveBakery   = isImpersonating ? impersonationContext.bakery   : currentBakery;

  // Show commercial landing page without header for non-logged users on main site
  // ONLY show commercial page if we're on the main domain without bakery context AND on main routes
  if (!bakeryContext.bakeryId && !currentUser && location.pathname === '/') {
    return (
      <AppShell>
        <CommercialLandingPage setView={(view) => navigate(view)} />
      </AppShell>
    );
  }

  return (
    <NotificationsProvider currentUser={currentUser} bakeryId={effectiveBakeryId}>
      <AppShell>
      <div className="app-header sticky top-0 z-40">
        {/* Impersonation Banner */}
        {isImpersonating && (
          <div className="w-full bg-amber-100 border-b-2 border-amber-500 px-6 py-2 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-amber-900 font-semibold text-sm">
              <Shield size={16} className="text-amber-600 flex-shrink-0" />
              <span>
                {language === 'fr' ? 'Mode admin — Consultation de ' : 'Admin View — Viewing as '}
                <strong>{impersonationContext.bakery?.name}</strong>
                {impersonationContext.bakerEmail && (
                  <span className="font-normal opacity-75"> ({impersonationContext.bakerEmail})</span>
                )}
              </span>
            </div>
            <Button variant="danger" size="sm" onClick={handleExitImpersonation}>
              <LogOut size={14} className="mr-1" />
              {language === 'fr' ? 'Quitter' : 'Exit'}
            </Button>
          </div>
        )}
        {/* Editorial Header - Compact & Refined */}
        {location.pathname !== '/signup-baker' && (
          <header className="bg-[var(--bg-secondary)] border-b border-[var(--border-subtle)] shadow-[var(--shadow-soft)] relative">
            <div className="container mx-auto px-6 py-4 relative">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {effectiveBakery?.logoUrl ? (
                    <div className="w-[51px] h-[51px] flex items-center justify-center">
                      <img
                        src={effectiveBakery.logoUrl}
                        alt="Logo"
                        className="w-full h-full object-contain rounded-lg"
                        onError={(e) => {
                          e.target.src = "/lockup-horizontal.svg";
                        }}
                      />
                    </div>
                  ) : (
                    <img
                      src="/lockup-horizontal.svg"
                      alt="Digi-Bakery"
                      className="h-7 w-auto object-contain"
                      style={{
                        filter: 'invert(54%) sepia(32%) saturate(727%) hue-rotate(332deg) brightness(91%) contrast(85%)'
                      }}
                    />
                  )}
                  {effectiveBakery?.name && (
                    <div>
                      <h1 className="font-display text-2xl font-bold text-[var(--text-primary)] tracking-tight leading-none">
                        {userProfile?.role === 'admin' && !isImpersonating
                          ? t('adminDashboard', language)
                          : effectiveBakery.name
                        }
                      </h1>
                      <p className="font-body-serif text-sm text-[var(--text-secondary)] italic mt-0.5">
                        {userProfile?.role === 'admin' && !isImpersonating
                          ? t('systemOverview', language)
                        : (effectiveBakery?.subtitle ||
                          (effectiveBakery?.name ?
                            (language === 'fr' ? 'Boulangerie artisanale' : 'Artisan Bakery') :
                            t('siteSubtitle', language)
                          ))
                      }
                    </p>
                  </div>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  {/* Language Switcher - Flag Style */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => language !== 'en' && toggleLanguage()}
                      className={`px-2 py-2 rounded-full transition-all duration-200 flex items-center justify-center ${
                        language === 'en'
                          ? 'bg-[var(--action-primary)] text-[var(--text-inverse)] shadow-[var(--shadow-soft)]'
                          : 'hover:bg-[var(--action-ghost-hover)] text-[var(--text-secondary)]'
                      }`}
                      title="Switch to English"
                    >
                      🇬🇧
                    </button>
                    <button
                      onClick={() => language !== 'fr' && toggleLanguage()}
                      className={`px-2 py-2 rounded-full transition-all duration-200 flex items-center justify-center ${
                        language === 'fr'
                          ? 'bg-[var(--action-primary)] text-[var(--text-inverse)] shadow-[var(--shadow-soft)]'
                          : 'hover:bg-[var(--action-ghost-hover)] text-[var(--text-secondary)]'
                      }`}
                      title="Passer au français"
                    >
                      🇫🇷
                    </button>
                  </div>

                  {/* Quick Theme Toggle (bakers only) */}
                  {(userProfile?.role === 'baker' || isImpersonating) && (
                    <button
                      onClick={handleHeaderThemeToggle}
                      className="px-2.5 py-2 rounded-full transition-all duration-200 flex items-center justify-center hover:bg-[var(--action-ghost-hover)] text-[var(--text-secondary)]"
                      title={language === 'fr' ? 'Changer le thème' : 'Toggle theme'}
                      aria-label={language === 'fr' ? 'Changer le thème' : 'Toggle theme'}
                    >
                      {document.documentElement.getAttribute('data-theme') === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
                    </button>
                  )}

                  {/* Notifications bell for bakers */}
                  {(userProfile?.role === 'baker' || isImpersonating) && (
                    <NotificationsBell bakerySubdomain={effectiveBakery?.subdomain || effectiveBakeryId} />
                  )}

                  {currentUser ? (
                    <div className="relative account-menu-container">
                      {/* Account Button - Editorial Style */}
                      <button
                        onClick={() => setShowAccountMenu(!showAccountMenu)}
                         variant="primary"
                        className="px-4 py-2 rounded-full transition-all duration-200 flex items-center gap-2"
                      >
                        <User size={16} />
                        <span className="hidden md:inline font-ui text-sm font-semibold">{t('account', language)}</span>
                        <ChevronDown size={14} className={`transition-transform duration-300 ${showAccountMenu ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Account Dropdown - Editorial Style */}
                      {showAccountMenu && (
                        <div className="nav-dropdown-panel menu-panel absolute right-0 top-full mt-2 w-56 rounded-2xl shadow-[var(--shadow-elevated)] z-50 p-2 border border-[var(--border-subtle)] text-[var(--text-primary)]">
                          {userProfile?.role !== 'admin' && (
                            <>
                              <button
                                onClick={() => {
                                  navigate(bakeryContext.bakeryId ? `/${currentBakery?.subdomain || currentBakeryId}/profile` : '/profile');
                                  setShowAccountMenu(false);
                                }}
                                className="nav-dropdown-item menu-item cursor-pointer w-full text-left px-4 py-2.5 rounded-xl transition-colors duration-200 flex items-center gap-3 font-ui text-sm font-semibold text-[var(--text-secondary)] hover:bg-[var(--action-ghost-hover)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                              >
                                <User size={16} className="text-[var(--text-secondary)]" />
                                {t('profile', language)}
                              </button>
                              <button
                                onClick={() => {
                                  navigate(bakeryContext.bakeryId ? `/${currentBakery?.subdomain || currentBakeryId}/orders` : '/orders');
                                  setShowAccountMenu(false);
                                }}
                                className="nav-dropdown-item menu-item cursor-pointer w-full text-left px-4 py-2.5 rounded-xl transition-colors duration-200 flex items-center gap-3 font-ui text-sm font-semibold text-[var(--text-secondary)] hover:bg-[var(--action-ghost-hover)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                              >
                                <ShoppingCart size={16} className="text-[var(--text-secondary)]" />
                                {t('myOrders', language)}
                              </button>
                              <div className="editorial-divider my-2 mx-4"></div>
                            </>
                          )}
                          {userProfile?.role === 'admin' && (
                            <>
                              <div className="px-4 py-2 font-body-serif text-sm text-[var(--text-secondary)] italic">
                                {userProfile.email}
                              </div>
                              <div className="editorial-divider my-2 mx-4"></div>
                            </>
                          )}
                          <button
                            onClick={handleLogout}
                            className="nav-dropdown-item menu-item cursor-pointer w-full text-left px-4 py-2.5 rounded-xl hover:bg-[var(--status-danger-soft)] text-[var(--status-danger)] transition-colors duration-200 flex items-center gap-3 font-ui text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                          >
                            <LogOut size={16} />
                            {t('logout', language)}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => navigate('/login')}
                       variant="primary"
                      className="px-6 py-2 rounded-full transition-all duration-200 flex items-center gap-2 font-ui text-sm font-semibold"
                    >
                      <User size={16} />
                      {t('login', language)}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </header>
        )}

        {/* Editorial Navigation Bar */}
        {!(['/signup-baker', '/login'].includes(location.pathname)) && (
          <nav className="bg-[var(--surface-elevated)] border-b border-[var(--border-subtle)] shadow-[var(--shadow-soft)]">
            <div className="container mx-auto px-6 py-3">
              <div className="md:hidden flex justify-end mb-2">
                <button
                  onClick={() => setIsMobileNavCollapsed(prev => !prev)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-[var(--border-subtle)] bg-[var(--bg-secondary)] text-[var(--text-primary)] text-sm font-semibold uppercase tracking-[0.03em] hover:bg-[var(--action-ghost-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] active:translate-y-[1px]"
                  aria-expanded={!isMobileNavCollapsed}
                  aria-label={isMobileNavCollapsed
                    ? (language === 'fr' ? 'Afficher le menu' : 'Show menu')
                    : (language === 'fr' ? 'Masquer le menu' : 'Hide menu')
                  }
                >
                  {isMobileNavCollapsed ? <Menu size={16} /> : <X size={16} />}
                  <span>{language === 'fr' ? 'Menu' : 'Menu'}</span>
                </button>
              </div>

              <div className={`${isMobileNavCollapsed ? 'hidden md:flex' : 'flex'} flex-wrap items-center gap-2`}>
                {(!userProfile || userProfile.role === 'customer') && (
                  <>
                    <button onClick={() => navigate(bakeryContext.bakeryId ? `/${bakeryContext.bakery?.subdomain || bakeryContext.bakeryId}` : '/home')} className={`${getNavButtonClasses(location.pathname === (bakeryContext.bakeryId ? `/${bakeryContext.bakery?.subdomain || bakeryContext.bakeryId}` : '/home'))}`}>
                      <Home size={18} />
                      {t('home', language)}
                    </button>
                    <button onClick={() => navigate(bakeryContext.bakeryId ? `/${bakeryContext.bakery?.subdomain || bakeryContext.bakeryId}/shop` : '/shop')} className={getNavButtonClasses(location.pathname.includes('/shop'))}>
                      <ShoppingCart size={18} />
                      {t('shop', language)}
                    </button>
                  </>
                )}

              {(userProfile?.role === 'baker' || isImpersonating) && (
                <>
                  <button onClick={() => navigate(isImpersonating ? `/${effectiveBakery?.subdomain || effectiveBakeryId}` : (bakeryContext.bakeryId ? `/${bakeryContext.bakery?.subdomain || currentBakeryId}` : '/home'))} className={`${getNavButtonClasses(location.pathname === (isImpersonating ? `/${effectiveBakery?.subdomain || effectiveBakeryId}` : (bakeryContext.bakeryId ? `/${bakeryContext.bakery?.subdomain || currentBakeryId}` : '/home')))} group`}>
                    <Home size={20} className="inline mr-2 group-hover:scale-110 transition-transform" />{t('home', language)}
                  </button>

                  {/* My Shop Menu */}
                  <div className="relative shop-menu-container">
                    <button
                      onClick={() => setShowShopMenu(!showShopMenu)}
                      className={`${getNavButtonClasses(['products', 'baker-orders', 'register-order', 'announcements', 'sales-reconciliation', 'pickup'].some(path => location.pathname.includes(path)))} flex items-center gap-2 group`}
                    >
                      {t('myShop', language)}
                      <ChevronDown size={16} className={`text-[var(--text-secondary)] transition-transform duration-300 ${showShopMenu ? 'rotate-180' : ''} group-hover:scale-110`} />
                    </button>
                    {showShopMenu && (
                      <div className="nav-dropdown-panel menu-panel absolute top-full left-0 mt-3 w-56 rounded-2xl shadow-[var(--shadow-elevated)] z-50 p-2 border border-[var(--border-subtle)] text-[var(--text-primary)]">
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/products`);
                            setShowShopMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.includes('/products'))}
                        >
                          {t('products', language)}
                        </button>
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/register-order`);
                            setShowShopMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.includes('/register-order'))}
                        >
                          {t('registerOrder', language) || 'Register Order'}
                        </button>
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/baker-orders`);
                            setShowShopMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.includes('/baker-orders'))}
                        >
                          {t('orders', language)}
                        </button>
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/customers`);
                            setShowShopMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.includes('/customers'))}
                        >
                          {t('customerManagementNav', language)}
                        </button>
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/pickup`);
                            setShowShopMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.includes('/pickup'))}
                        >
                          {t('marketModeTitle', language)}
                        </button>
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/sales-reconciliation`);
                            setShowShopMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.includes('/sales-reconciliation'))}
                        >
                          {t('salesReconciliation', language)}
                        </button>
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/announcements`);
                            setShowShopMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.includes('/announcements'))}
                        >
                          {t('announcements', language) || 'Announcements'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* My Bakery Menu */}
                  <div className="relative bakery-menu-container">
                    <button
                      onClick={() => setShowBakeryMenu(!showBakeryMenu)}
                      className={`${getNavButtonClasses(['stock', 'recipes', 'equipment', 'fixed-costs'].some(path => location.pathname.includes(path)))} flex items-center gap-2 group`}
                    >
                      {t('myBakery', language)}
                      <ChevronDown size={16} className={`text-[var(--text-secondary)] transition-transform duration-300 ${showBakeryMenu ? 'rotate-180' : ''} group-hover:scale-110`} />
                    </button>
                    {showBakeryMenu && (
                      <div className="nav-dropdown-panel menu-panel absolute top-full left-0 mt-3 w-56 rounded-2xl shadow-[var(--shadow-elevated)] z-50 p-2 border border-[var(--border-subtle)] text-[var(--text-primary)]">
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/stock`);
                            setShowBakeryMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.includes('/stock'))}
                        >
                          {t('stock', language)}
                        </button>
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/recipes`);
                            setShowBakeryMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.includes('/recipes'))}
                        >
                          {t('recipes', language)}
                        </button>
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/equipment`);
                            setShowBakeryMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.includes('/equipment'))}
                        >
                          {t('equipment', language)}
                        </button>
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/fixed-costs`);
                            setShowBakeryMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.includes('/fixed-costs'))}
                        >
                          {t('fixedCosts', language)}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* My Production Menu */}
                  <div className="relative production-menu-container">
                    <button
                      onClick={() => setShowProductionMenu(!showProductionMenu)}
                      className={`${getNavButtonClasses(['production', 'templates', 'production-planning', 'shopping-list'].some(path => location.pathname.includes(path)))} flex items-center gap-2 group`}
                    >
                      {t('myProduction', language)}
                      <ChevronDown size={16} className={`text-[var(--text-secondary)] transition-transform duration-300 ${showProductionMenu ? 'rotate-180' : ''} group-hover:scale-110`} />
                    </button>
                    {showProductionMenu && (
                      <div className="nav-dropdown-panel menu-panel absolute top-full left-0 mt-3 w-56 rounded-2xl shadow-[var(--shadow-elevated)] z-50 p-2 border border-[var(--border-subtle)] text-[var(--text-primary)]">
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/production`);
                            setShowProductionMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.endsWith('/production'))}
                        >
                          {t('productionDays', language)}
                        </button>
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/production-planning`);
                            setShowProductionMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.includes('/production-planning'))}
                        >
                          {t('productionPlanning', language)}
                        </button>
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/shopping-list`);
                            setShowProductionMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.includes('/shopping-list'))}
                        >
                          {t('shoppingList', language)}
                        </button>
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/templates`);
                            setShowProductionMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.includes('/templates'))}
                        >
                          {t('templates', language)}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Reports Menu */}
                  <div className="relative reports-menu-container">
                    <button
                      onClick={() => setShowReportsMenu(!showReportsMenu)}
                      className={`${getNavButtonClasses(['reports', 'waste-reports', 'inventory-adjustments', 'shop-analytics', 'weather-analysis'].some(path => location.pathname.includes(path)))} flex items-center gap-2 group`}
                    >
                      <FileText size={20} className="inline mr-2 group-hover:scale-110 transition-transform" />{t('reports', language)}
                      <ChevronDown size={16} className={`text-[var(--text-secondary)] transition-transform duration-300 ${showReportsMenu ? 'rotate-180' : ''} group-hover:scale-110`} />
                    </button>
                    {showReportsMenu && (
                      <div className="nav-dropdown-panel menu-panel absolute top-full left-0 mt-3 w-56 rounded-2xl shadow-[var(--shadow-elevated)] z-50 p-2 border border-[var(--border-subtle)] text-[var(--text-primary)]">
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/reports`);
                            setShowReportsMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.endsWith('/reports'))}
                        >
                          {t('salesReports', language)}
                        </button>
                        {effectiveBakery?.weatherTracking === true && (
                          <button
                            onClick={() => {
                              navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/weather-analysis`);
                              setShowReportsMenu(false);
                            }}
                            className={getMenuButtonClasses(location.pathname.includes('/weather-analysis'))}
                          >
                            {t('weatherAnalysisMenu', language)}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/waste-reports`);
                            setShowReportsMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.includes('/waste-reports'))}
                        >
                          {t('wasteReport', language)}
                        </button>
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/inventory-adjustments`);
                            setShowReportsMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.includes('/inventory-adjustments'))}
                        >
                          {t('inventoryAdjustments', language)}
                        </button>
                        <button
                          onClick={() => {
                            navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/shop-analytics`);
                            setShowReportsMenu(false);
                          }}
                          className={getMenuButtonClasses(location.pathname.includes('/shop-analytics'))}
                        >
                          {t('shopAnalytics', language) || (language === 'fr' ? 'Visiteurs boutique' : 'Shop Visitors')}
                        </button>
                      </div>
                    )}
                  </div>
                  <button onClick={() => navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId || userProfile?.bakeryId}/settings`)} className={`${getNavButtonClasses(location.pathname.includes('/settings'))} group`}>
                    <Settings size={20} className="inline mr-2 group-hover:scale-110 transition-transform" />{t('settings', language)}
                  </button>
                  <button onClick={() => navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/help`)} className={`${getNavButtonClasses(location.pathname.includes('/help'))} group`}>
                    <HelpCircle size={20} className="inline mr-2 group-hover:scale-110 transition-transform" />{language === 'fr' ? 'Aide' : 'Help'}
                  </button>
                </>
              )}

              {/* Orders button for customers */}
              {currentUser && userProfile?.role === 'customer' && (
                <button onClick={() => navigate(bakeryContext.bakeryId ? `/${currentBakery?.subdomain || currentBakeryId}/orders` : '/orders')} className={`${getNavButtonClasses(location.pathname.includes('/orders'))} group`}>
                  <FileText size={20} className="inline mr-2 group-hover:scale-110 transition-transform" />{t('orders', language)}
                </button>
              )}

              {/* Admin navigation */}
              {userProfile?.role === 'admin' && !isImpersonating && (
                <>
                  <button onClick={() => navigate('/admin')} className={`${getNavButtonClasses(location.pathname === '/admin')} group`}>
                    <Shield size={20} className="inline mr-2 group-hover:scale-110 transition-transform" />{t('adminDashboard', language)}
                  </button>
                  <button onClick={() => navigate('/admin/bakeries')} className={`${getNavButtonClasses(location.pathname.includes('/admin/bakeries'))} group`}>
                    <ShoppingCart size={20} className="inline mr-2 group-hover:scale-110 transition-transform" />{t('bakeryManagement', language)}
                  </button>
                  <button onClick={() => navigate('/admin/orders')} className={`${getNavButtonClasses(location.pathname.includes('/admin/orders'))} group`}>
                    <FileText size={20} className="inline mr-2 group-hover:scale-110 transition-transform" />{t('orderManagement', language)}
                  </button>
                  <button onClick={() => navigate('/admin/settings')} className={`${getNavButtonClasses(location.pathname === '/admin/settings')} group`}>
                    <User size={20} className="inline mr-2 group-hover:scale-110 transition-transform" />{t('adminSettings', language)}
                  </button>
                  <button onClick={() => navigate('/admin/database-cleaner')} className={`${getNavButtonClasses(location.pathname.includes('/admin/database-cleaner'))} group`}>
                    <Settings size={20} className="inline mr-2 group-hover:scale-110 transition-transform" />DB Cleaner
                  </button>
                </>
              )}
            </div>
          </div>
        </nav>
        )}
      </div>

      {/* Mobile Bottom Navigation — baker only, md:hidden */}
      {(userProfile?.role === 'baker' || isImpersonating) && !(['/signup-baker', '/login'].includes(location.pathname)) && (
        <>
          <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[var(--surface-elevated)] border-t border-[var(--border-subtle)] shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
            <div className="flex items-stretch h-16">
              <button
                onClick={() => navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/baker-orders`)}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors active:scale-95 ${
                  location.pathname.includes('/baker-orders')
                    ? 'text-[var(--action-primary)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                }`}
              >
                <FileText size={20} />
                <span className="text-[10px] font-semibold leading-tight">{language === 'fr' ? 'Commandes' : 'Orders'}</span>
              </button>

              <button
                onClick={() => navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/register-order`)}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors active:scale-95 ${
                  location.pathname.includes('/register-order')
                    ? 'text-[var(--action-primary)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                }`}
              >
                <Plus size={20} />
                <span className="text-[10px] font-semibold leading-tight">{language === 'fr' ? 'Nouvelle' : 'New Order'}</span>
              </button>

              <button
                onClick={() => navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/production`)}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors active:scale-95 ${
                  location.pathname.endsWith('/production')
                    ? 'text-[var(--action-primary)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                }`}
              >
                <Calendar size={20} />
                <span className="text-[10px] font-semibold leading-tight">{language === 'fr' ? 'Production' : 'Prod. Days'}</span>
              </button>

              <button
                onClick={() => navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/production-planning`)}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors active:scale-95 ${
                  location.pathname.includes('/production-planning')
                    ? 'text-[var(--action-primary)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                }`}
              >
                <BarChart2 size={20} />
                <span className="text-[10px] font-semibold leading-tight">{language === 'fr' ? 'Planning' : 'Planning'}</span>
              </button>

              <button
                onClick={() => setShowMobileMore(true)}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors active:scale-95 ${
                  showMobileMore
                    ? 'text-[var(--action-primary)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                }`}
              >
                <MoreHorizontal size={20} />
                <span className="text-[10px] font-semibold leading-tight">{language === 'fr' ? 'Plus' : 'More'}</span>
              </button>
            </div>
          </nav>

          {/* More sheet */}
          {showMobileMore && (
            <div className="md:hidden fixed inset-0 z-50" onClick={() => setShowMobileMore(false)}>
              <div className="absolute inset-0 bg-black/40" />
              <div
                className="absolute bottom-0 left-0 right-0 bg-[var(--surface-card)] rounded-t-2xl shadow-[var(--shadow-elevated)] p-4 pb-10"
                onClick={e => e.stopPropagation()}
              >
                <div className="w-10 h-1 bg-[var(--border-strong)] rounded-full mx-auto mb-5" />
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { label: language === 'fr' ? 'Produits' : 'Products', icon: ShoppingCart, path: 'products' },
                    { label: language === 'fr' ? 'Paramètres' : 'Settings', icon: Settings, path: 'settings' },
                    { label: language === 'fr' ? 'Rapports' : 'Reports', icon: FileText, path: 'reports' },
                    { label: language === 'fr' ? 'Stock' : 'Stock', icon: BarChart2, path: 'stock' },
                    { label: language === 'fr' ? 'Aide' : 'Help', icon: HelpCircle, path: 'help' },
                    { label: language === 'fr' ? 'Recettes' : 'Recipes', icon: FileText, path: 'recipes' },
                    { label: language === 'fr' ? 'Abandon' : 'Waste', icon: AlertCircle, path: 'waste-reports' },
                    { label: language === 'fr' ? 'Ajustements' : 'Adjustments', icon: BarChart2, path: 'inventory-adjustments' },
                    { label: language === 'fr' ? 'Visiteurs' : 'Visitors', icon: BarChart2, path: 'shop-analytics' },
                    ...(effectiveBakery?.weatherTracking === true
                      ? [{ label: language === 'fr' ? 'Météo' : 'Weather', icon: Sun, path: 'weather-analysis' }]
                      : []),
                    { label: language === 'fr' ? 'Annonces' : 'Announce.', icon: AlertCircle, path: 'announcements' },
                    { label: language === 'fr' ? 'Courses' : 'Shopping', icon: ShoppingCart, path: 'shopping-list' },
                    { label: language === 'fr' ? 'Clients' : 'Customers', icon: Users, path: 'customers' },
                  ].map(({ label, icon: Icon, path }) => (
                    <button
                      key={path}
                      onClick={() => {
                        navigate(`/${effectiveBakery?.subdomain || effectiveBakeryId}/${path}`);
                        setShowMobileMore(false);
                      }}
                      className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-[var(--bg-secondary)] hover:bg-[var(--action-ghost-hover)] text-[var(--text-primary)] transition-colors active:scale-95"
                    >
                      <Icon size={20} />
                      <span className="text-[10px] font-semibold text-center leading-tight">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Main Content */}
      <main className={`container mx-auto px-4 py-8 ${(userProfile?.role === 'baker' || isImpersonating) ? 'pb-24 md:pb-8' : ''}`}>
        {/* Grace Period Banner - Persistent across all baker pages */}
        {userProfile?.role === 'baker' && showGracePeriod && gracePeriodInfo && (
          <Alert tone="warning" className="w-full shadow-sm border-b rounded-none">
            <div className="max-w-7xl mx-auto px-0 py-0">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-start flex-1">
                  <AlertCircle className="text-[var(--status-warning)] mr-3 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="text-[var(--text-primary)] font-semibold text-sm">
                      {t('gracePeriodActive', language)}{' '}—{' '}
                      {gracePeriodInfo.daysRemaining > 0
                        ? `${gracePeriodInfo.daysRemaining} ${t('daysRemainingToSubscribe', language)}`
                        : t('gracePeriodEndsToday', language)
                      }
                    </p>
                    <p className="text-[var(--text-secondary)] text-xs mt-0.5">
                      {t('gracePeriodMessage', language)}
                    </p>
                    <p className="text-[var(--text-secondary)] text-xs mt-1">
                      👉 {t('gracePeriodBullet1', language)} &nbsp;·&nbsp; 👉 {t('gracePeriodBullet2', language)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/${currentBakery?.subdomain || currentBakeryId}/settings?tab=subscription`)}
                  className="ml-4 px-4 py-2 rounded-full border border-[var(--status-warning)] text-[var(--status-warning)] bg-transparent hover:bg-[var(--status-warning)] hover:text-white transition-colors font-semibold text-sm whitespace-nowrap flex items-center"
                >
                  <CreditCard className="mr-2" size={16} />
                  {t('subscribeNow', language)}
                </button>
              </div>
            </div>
          </Alert>
        )}

        {/* Location Reminder - postcode + town are mandatory; shown on every baker page until filled in */}
        {userProfile?.role === 'baker' && !isImpersonating && currentBakery
          && (!currentBakery.postalCode || !currentBakery.city)
          && !location.pathname.includes('/settings') && (
          <Alert tone="info" className="w-full shadow-sm border-b rounded-none mb-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-start flex-1 min-w-0">
                <MapPin className="text-[var(--action-primary)] mr-3 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-[var(--text-primary)] font-semibold text-sm">{t('locationReminderTitle', language)}</p>
                  <p className="text-[var(--text-secondary)] text-xs mt-0.5">{t('locationReminderText', language)}</p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/${currentBakery?.subdomain || currentBakeryId}/settings?focus=location`)}
                className="px-4 py-2 rounded-full border border-[var(--action-primary)] text-[var(--action-primary)] bg-transparent hover:bg-[var(--action-primary)] hover:text-[var(--text-on-accent)] transition-colors font-semibold text-sm whitespace-nowrap flex items-center"
              >
                <MapPin className="mr-2" size={16} />
                {t('locationReminderButton', language)}
              </button>
            </div>
          </Alert>
        )}

        <ErrorBoundary>
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]">
            <div className="text-center">
              <img src="/Logo-digi-bakery-512.png" alt="Logo" className="w-24 h-24 mb-4 animate-bounce mx-auto" />
              <p className="text-xl text-[var(--text-secondary)] font-medium">Loading...</p>
            </div>
          </div>
        }>
        <Routes>
          {/* Commercial routes - show based on whether we have bakery context */}
          <Route path="/" element={
            bakeryContext.routingMethod === undefined
              ? null
              : bakeryContext.bakeryId && bakeryContext.isCustomerFacing
                ? <HomeView goToShop={goToShop} userProfile={userProfile} currentBakery={currentBakery} bakerSettings={bakerSettings} />
                : <CommercialLandingPage setView={(view) => navigate(view)} />
          } />
          <Route path="/commercial-landing" element={<CommercialLandingPage setView={(view) => navigate(view)} />} />
          <Route path="/login" element={<AuthScreen setView={(view) => navigate(view === 'login' ? '/login' : '/signup-baker')} view={location.pathname === '/signup-baker' ? 'signup-baker' : 'login'} allowBakerRegistration={location.pathname === '/signup-baker'} />} />
          <Route path="/signup-baker" element={<AuthScreen setView={(view) => navigate(view === 'login' ? '/login' : '/signup-baker')} view="signup-baker" allowBakerRegistration={true} />} />
          <Route path="/reset-password" element={<PasswordResetPage />} />
          <Route path="/home" element={<HomeView goToShop={goToShop} userProfile={userProfile} currentBakery={currentBakery} bakerSettings={bakerSettings} />} />
          <Route path="/shop" element={
            <ShopView
              currentUser={currentUser}
              userProfile={userProfile}
              setView={(view) => navigate(bakeryContext.routingMethod === 'subdomain' ? `/${view}` : view)}
              bakeryId={effectiveBakeryId}
              bakery={currentBakery}
              bakerSettings={bakerSettings}
            />
          } />
          <Route path="/orders" element={currentUser ? <OrdersView currentUser={currentUser} bakeryId={effectiveBakeryId} /> : <Navigate to="/login" />} />
          <Route path="/profile" element={currentUser ? <ProfileView currentUser={currentUser} userProfile={userProfile} setUserProfile={setUserProfile} /> : <Navigate to="/login" />} />
          <Route path="/order-confirmation" element={<OrderConfirmationView setView={(view) => navigate(view)} orderData={orderConfirmationDataRef.current} />} />
          <Route path="/payment" element={<PaymentForm />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-canceled" element={<PaymentCanceled />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          {/* Admin routes - only accessible to admin users */}
          <Route path="/admin" element={currentUser && userProfile?.role === 'admin' ? <AdminDashboard currentUser={currentUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/bakeries" element={currentUser && userProfile?.role === 'admin' ? <AdminBakeriesView currentUser={currentUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/bakeries/:bakeryId" element={currentUser && userProfile?.role === 'admin' ? <AdminBakeryDetail currentUser={currentUser} onStartImpersonation={handleStartImpersonation} /> : <Navigate to="/login" />} />
          <Route path="/admin/users" element={currentUser && userProfile?.role === 'admin' ? <AdminUsersView currentUser={currentUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/analytics" element={currentUser && userProfile?.role === 'admin' ? <AdminAnalytics currentUser={currentUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/orders" element={currentUser && userProfile?.role === 'admin' ? <AdminOrdersView currentUser={currentUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/settings" element={currentUser && userProfile?.role === 'admin' ? <AdminProfileSettings currentUser={currentUser} userProfile={userProfile} /> : <Navigate to="/login" />} />
          <Route path="/admin/database-cleaner" element={currentUser && userProfile?.role === 'admin' ? <DatabaseCleaner currentUser={currentUser} /> : <Navigate to="/login" />} />

          {/* Bakery-specific routes */}
          <Route path="/:bakerySubdomain" element={<HomeView goToShop={goToShop} userProfile={userProfile} currentBakery={currentBakery} bakerSettings={bakerSettings} />} />
          <Route path="/:bakerySubdomain/shop" element={
            <ShopView currentUser={currentUser} userProfile={userProfile} setView={(view) => navigate(`/${params.bakerySubdomain}/${view}`)} bakeryId={effectiveBakeryId} bakery={currentBakery} bakerSettings={bakerSettings} />
          } />
          <Route path="/:bakerySubdomain/orders" element={currentUser ? <OrdersView currentUser={currentUser} bakeryId={effectiveBakeryId} /> : <Navigate to="/login" />} />
          <Route path="/:bakerySubdomain/profile" element={currentUser ? <ProfileView currentUser={currentUser} userProfile={userProfile} setUserProfile={setUserProfile} /> : <Navigate to="/login" />} />
          <Route path="/:bakerySubdomain/notifications" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <BakerNotificationsView />
            </ProtectedBakerRoute>
          } />

          {/* Baker-specific routes - Protected by subscription */}
          <Route path="/:bakerySubdomain/products" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <ProductManagement currentUser={currentUser} bakeryId={effectiveBakeryId} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/register-order" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <BakerOrderRegistration currentUser={currentUser} bakeryId={effectiveBakeryId} setView={(view) => navigate(`/${params.bakerySubdomain}/${view}`)} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/recipes" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <RecipeManager currentUser={currentUser} bakeryId={effectiveBakeryId} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/stock" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <StockManagement currentUser={currentUser} bakeryId={effectiveBakeryId} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/physical-inventory" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <PhysicalInventory currentUser={currentUser} bakeryId={effectiveBakeryId} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/inventory-adjustments" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <InventoryAdjustmentsView currentUser={currentUser} bakeryId={effectiveBakeryId} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/equipment" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <EquipmentManager currentUser={currentUser} bakeryId={effectiveBakeryId} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/fixed-costs" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <FixedCostsManagement currentUser={currentUser} bakeryId={effectiveBakeryId} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/production" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <ProductionDaysManagement currentUser={currentUser} bakeryId={effectiveBakeryId} setView={(view) => navigate(`/${params.bakerySubdomain}/${view}`)} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/templates" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <ProductionTemplates currentUser={currentUser} bakeryId={effectiveBakeryId} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/shopping-list" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <ShoppingList bakeryId={effectiveBakeryId} currentBakery={effectiveBakery} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/production-planning" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <ProductionPlanning currentUser={currentUser} bakeryId={effectiveBakeryId} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/baker-orders" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <BakerOrdersView currentUser={currentUser} bakeryId={effectiveBakeryId} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/customers" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <CustomerManagement currentUser={currentUser} bakeryId={effectiveBakeryId} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/accept-customer-invite" element={
            <AcceptCustomerInvite currentUser={currentUser} />
          } />
          <Route path="/:bakerySubdomain/pickup" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <PickupMode currentUser={currentUser} bakeryId={effectiveBakeryId} currentBakery={effectiveBakery} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/sales-reconciliation" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <SalesReconciliation currentUser={currentUser} bakeryId={effectiveBakeryId} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/reports" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <ReportsView currentUser={currentUser} bakeryId={effectiveBakeryId} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/weather-analysis" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <WeatherSalesAnalysis currentUser={currentUser} bakeryId={effectiveBakeryId} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/waste-reports" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <WasteReportsView currentUser={currentUser} bakeryId={effectiveBakeryId} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/shop-analytics" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <ShopAnalyticsView currentUser={currentUser} bakeryId={effectiveBakeryId} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/settings" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <BakerSettings currentUser={currentUser} onSettingsSaved={handleSettingsSaved} setView={(view) => navigate(view === 'home-page-editor' ? `/${params.bakerySubdomain}/${view}` : view)} bakeryId={effectiveBakeryId} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/help" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <HelpView currentUser={currentUser} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/home-page-editor" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <HomePageEditor currentUser={currentUser} bakeryId={effectiveBakeryId} setView={(view) => navigate(`/${params.bakerySubdomain}/${view}`)} onSave={handleSettingsSaved} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/announcements" element={
            <ProtectedBakerRoute currentUser={currentUser} userProfile={userProfile} currentBakery={effectiveBakery} isAdminImpersonating={isImpersonating}>
              <AnnouncementManager currentUser={currentUser} bakeryId={effectiveBakeryId} />
            </ProtectedBakerRoute>
          } />
          <Route path="/:bakerySubdomain/order-confirmation" element={<OrderConfirmationView setView={(view) => navigate(bakeryContext.bakeryId ? (view === 'home' ? `/${bakeryContext.bakery?.subdomain || bakeryContext.bakeryId}` : `/${bakeryContext.bakery?.subdomain || bakeryContext.bakeryId}/${view}`) : view)} orderData={orderConfirmationDataRef.current} />} />
          <Route path="/:bakerySubdomain/payment" element={<PaymentForm />} />
          <Route path="/:bakerySubdomain/payment-success" element={<PaymentSuccess />} />
          <Route path="/:bakerySubdomain/payment-canceled" element={<PaymentCanceled />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </main>

      {/* Editorial Footer - Subtle & Refined */}
      {!(['/signup-baker', '/login'].includes(location.pathname)) && (
        <footer className="bg-[var(--bg-secondary)] border-t border-[var(--border-subtle)] mt-auto">
          <div className="container mx-auto px-6 py-3">
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 text-xs">
              <div className="font-body-serif text-[var(--text-secondary)] italic">
                digi-bakery v2.3.2 · {language === 'fr' ? 'Boulangerie artisanale numérique' : 'Artisan Digital Bakery'}
              </div>
              <button
                onClick={() => navigate('/privacy-policy')}
                className="font-ui text-[var(--text-secondary)] hover:text-[var(--action-primary)] transition-colors underline"
              >
                {language === 'fr' ? 'Politique de confidentialité' : 'Privacy Policy'}
              </button>
            </div>
          </div>
        </footer>
      )}
      </AppShell>
    </NotificationsProvider>
  );
}

export default AppContent;
